import { ref, computed, onBeforeUnmount } from 'vue';
import {
  createSessionApi,
  stopSessionApi,
  uploadChunkApi,
  deleteSessionApi,
} from '@/api/recordingApi';

const AUTO_SAVE_INTERVAL_MS = 30000;
const UPLOAD_QUEUE_WAIT_TIMEOUT_MS = 30000;
const FINAL_CHUNK_WAIT_TIMEOUT_MS = 10000;

/**
 * Composable for managing live recording state machine, MediaRecorder,
 * chunk uploads, and timer.
 *
 * @param {Object} options
 * @param {import('vue').Ref<string>} options.patientId
 * @param {import('vue').Ref<string>} options.encounterNoteId
 * @param {Function} options.onSessionCreated - Called with session object when a new session is created
 * @param {Function} options.onRecordingFinished - Called when recording finishes successfully
 * @param {Function} options.onError - Called with error message
 */
export function useRecording({ patientId, encounterNoteId, onSessionCreated, onRecordingFinished, onError }) {
  // --- State ---
  const recordingState = ref(''); // '' (idle) | 'recording' | 'paused'
  const elapsedSeconds = ref(0);
  const recordingDuration = ref(0);
  const recordingError = ref('');
  const isLocalRecording = ref(false);

  // Media
  const mediaRecorder = ref(null);
  const audioChunks = ref([]);
  const tempRecordingBlob = ref(null);
  const tempRecordingUrl = ref('');

  // Session
  const sessionId = ref(null);
  const sessionStatus = ref('idle'); // idle | creating | active | paused | completing | completed | error

  // Upload queue
  const sequenceNumber = ref(0);
  const isUploading = ref(false);
  const uploadQueue = ref([]);
  let isProcessingQueue = false;
  const isFinishing = ref(false);
  const uploadErrorCount = ref(0);
  let resolveFinalChunkQueued = null;
  let finalChunkObserved = false;

  // Timer
  let timerId = null;

  // Hover state (for template bindings)
  const isRecordingHovered = ref(false);
  const isFinishHovered = ref(false);

  const hasMediaSupport = computed(
    () => typeof window !== 'undefined' && 'MediaRecorder' in window && navigator?.mediaDevices?.getUserMedia
  );

  const formattedElapsed = computed(() => {
    const minutes = String(Math.floor(elapsedSeconds.value / 60)).padStart(2, '0');
    const seconds = String(elapsedSeconds.value % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  });

  // --- Timer ---
  const startTimer = () => {
    stopTimer();
    timerId = setInterval(() => {
      elapsedSeconds.value += 1;
    }, 1000);
  };

  const stopTimer = () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  // --- Temp recording ---
  const clearTempRecording = () => {
    if (tempRecordingUrl.value) {
      URL.revokeObjectURL(tempRecordingUrl.value);
    }
    tempRecordingUrl.value = '';
    tempRecordingBlob.value = null;
  };

  const stopStream = () => {
    const stream = mediaRecorder.value?.stream;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  // --- Upload queue ---
  const uploadRecordingChunk = async (blob, isFinal = false) => {
    const currentSequence = sequenceNumber.value;
    sequenceNumber.value += 1;
    uploadQueue.value.push({ blob, sequenceNumber: currentSequence, isFinal });
    processUploadQueue();
  };

  const processUploadQueue = async () => {
    if (isProcessingQueue || uploadQueue.value.length === 0) return;
    isProcessingQueue = true;

    while (uploadQueue.value.length > 0) {
      const { blob, sequenceNumber: seqNum, isFinal } = uploadQueue.value.shift();
      isUploading.value = true;
      try {
        const formData = new FormData();
        formData.append('audio', blob, `chunk-${seqNum}.webm`);
        formData.append('sequence_number', String(seqNum));
        formData.append('duration_ms', String(AUTO_SAVE_INTERVAL_MS));
        formData.append('is_final', String(isFinal));
        await uploadChunkApi(sessionId.value, formData);
      } catch (error) {
        console.error(`Failed to upload recording chunk ${seqNum}:`, error);
        uploadErrorCount.value += 1;
      } finally {
        isUploading.value = uploadQueue.value.length > 0;
      }
    }
    isProcessingQueue = false;
  };

  const waitForPendingUploads = ({ timeoutMs = UPLOAD_QUEUE_WAIT_TIMEOUT_MS } = {}) => {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const checkComplete = () => {
        const now = Date.now();
        const hasPendingUploads = uploadQueue.value.length > 0 || isProcessingQueue;
        if (!hasPendingUploads) {
          resolve();
        } else if (now - startedAt >= timeoutMs) {
          reject(new Error('Timed out waiting for recording uploads to complete.'));
        } else {
          setTimeout(checkComplete, 100);
        }
      };
      checkComplete();
    });
  };

  // --- Recording lifecycle ---
  const startRecording = async (recordCallback) => {
    recordingError.value = '';
    if (!hasMediaSupport.value) {
      recordingError.value = 'Recording not supported in this browser.';
      return;
    }

    try {
      sessionStatus.value = 'creating';
      const sessionResponse = await createSessionApi({
        patient_id: patientId.value,
        encounter_id: encounterNoteId.value,
      });
      sessionId.value = sessionResponse.id;
      sessionStatus.value = 'active';

      // Notify parent to push session into session list
      onSessionCreated?.({ id: sessionResponse.id });

      // Start media recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      clearTempRecording();
      audioChunks.value = [];
      sequenceNumber.value = 0;
      uploadQueue.value = [];
      uploadErrorCount.value = 0;
      resolveFinalChunkQueued = null;
      finalChunkObserved = false;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorder.value = recorder;

      recorder.ondataavailable = (event) => {
        if (isFinishing.value && resolveFinalChunkQueued) {
          finalChunkObserved = true;
          resolveFinalChunkQueued();
          resolveFinalChunkQueued = null;
        }
        if (event.data && event.data.size > 0) {
          audioChunks.value.push(event.data);
          if (sessionId.value) {
            uploadRecordingChunk(event.data, isFinishing.value);
          }
        }
      };

      recorder.onstop = () => {
        stopTimer();
        stopStream();
        if (!audioChunks.value.length) {
          recordingState.value = '';
          return;
        }
        const blob = new Blob(audioChunks.value, { type: 'audio/webm' });
        tempRecordingBlob.value = blob;
        tempRecordingUrl.value = URL.createObjectURL(blob);
        recordingState.value = '';
        elapsedSeconds.value = 0;
      };

      recorder.start(AUTO_SAVE_INTERVAL_MS);
      elapsedSeconds.value = 0;
      recordingState.value = 'recording';
      startTimer();
      recordCallback?.();
    } catch (error) {
      console.error('Failed to start recording', error);
      sessionStatus.value = 'error';
      recordingError.value = error.message || 'Failed to start recording session.';
      recordingState.value = '';
      stopTimer();
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.value && mediaRecorder.value.state === 'recording') {
      mediaRecorder.value.pause();
      recordingState.value = 'paused';
      stopTimer();
    }
  };

  const resumeRecording = (recordCallback) => {
    if (mediaRecorder.value && mediaRecorder.value.state === 'paused') {
      mediaRecorder.value.resume();
      recordingState.value = 'recording';
      startTimer();
      recordCallback?.();
    }
  };

  const finishRecording = async () => {
    isFinishing.value = true;
    finalChunkObserved = false;
    const finalChunkQueuedPromise = new Promise((resolve) => {
      resolveFinalChunkQueued = resolve;
    });
    stopTimer();
    recordingDuration.value = elapsedSeconds.value;
    isLocalRecording.value = true;

    if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
      await new Promise((resolve) => {
        const originalOnStop = mediaRecorder.value.onstop;
        mediaRecorder.value.onstop = (event) => {
          if (originalOnStop) originalOnStop.call(mediaRecorder.value, event);
          resolve();
        };
        mediaRecorder.value.stop();
      });
    } else {
      stopStream();
    }

    try {
      if (!finalChunkObserved) {
        await Promise.race([
          finalChunkQueuedPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timed out waiting for final chunk event.')), FINAL_CHUNK_WAIT_TIMEOUT_MS)
          ),
        ]);
      }
      await waitForPendingUploads();
    } catch (error) {
      console.error('Pending upload wait failed:', error);
      recordingState.value = '';
      elapsedSeconds.value = 0;
      isFinishing.value = false;
      sessionStatus.value = 'error';
      recordingError.value = 'Timed out waiting for recording upload. Please try again.';
      return;
    }

    if (uploadErrorCount.value > 0) {
      recordingState.value = '';
      elapsedSeconds.value = 0;
      isFinishing.value = false;
      recordingError.value = `Failed to upload ${uploadErrorCount.value} recording chunk(s). Please try again.`;
      sessionStatus.value = 'error';
      return;
    }

    if (audioChunks.value.length && !tempRecordingUrl.value) {
      const blob = new Blob(audioChunks.value, { type: 'audio/webm' });
      tempRecordingBlob.value = blob;
      tempRecordingUrl.value = URL.createObjectURL(blob);
    }

    recordingState.value = '';
    elapsedSeconds.value = 0;
    isFinishing.value = false;

    if (sessionId.value) {
      try {
        sessionStatus.value = 'completing';
        await stopSessionApi(sessionId.value);
        sessionStatus.value = 'completed';
        onRecordingFinished?.();
      } catch (error) {
        console.error('Failed to stop session:', error);
        sessionStatus.value = 'error';
        recordingError.value = 'Failed to stop recording session. Please try again.';
      }
    }
  };

  /**
   * Reset all recording state (for starting a new recording or cleanup)
   */
  const resetRecordingState = () => {
    const sid = sessionId.value;
    if (sid) {
      deleteSessionApi(sid).catch((error) => {
        console.error('Failed to delete session:', error);
      });
    }

    recordingState.value = '';
    elapsedSeconds.value = 0;
    recordingDuration.value = 0;
    recordingError.value = '';
    isLocalRecording.value = false;
    sessionId.value = null;
    sessionStatus.value = 'idle';
    isFinishing.value = false;
    resolveFinalChunkQueued = null;
    finalChunkObserved = false;
    uploadErrorCount.value = 0;
    audioChunks.value = [];
    sequenceNumber.value = 0;
    uploadQueue.value = [];
    clearTempRecording();
  };

  // --- Cleanup ---
  onBeforeUnmount(() => {
    stopTimer();
    if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
      mediaRecorder.value.stop();
    }
    stopStream();
    clearTempRecording();
    uploadQueue.value = [];
  });

  return {
    // State
    recordingState,
    elapsedSeconds,
    recordingDuration,
    recordingError,
    isLocalRecording,
    sessionId,
    sessionStatus,
    tempRecordingBlob,
    tempRecordingUrl,
    isRecordingHovered,
    isFinishHovered,

    // Computed
    hasMediaSupport,
    formattedElapsed,

    // Methods
    startRecording,
    pauseRecording,
    resumeRecording,
    finishRecording,
    resetRecordingState,
    clearTempRecording,
    waitForPendingUploads,
  };
}
